// HTML whitelist configuration
const allowedTags = new Set([
  'p', 'br', 'strong', 'b', 'em', 'i', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'ul', 'ol', 'li', 'a', 'blockquote', 'code', 'pre', 'span', 'div'
]);

const allowedAttributes: Record<string, Set<string>> = {
  a: new Set(['href', 'title', 'target', 'rel']),
};

const skippedTags = new Set([
  'script', 'style', 'iframe', 'object', 'embed', 'link', 'meta'
]);

/**
   * Sanitizes HTML content using a whitelist approach
   */
export function sanitizeHtml(html: string): string {
  if (!html) return '';

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  sanitizeNode(doc.body, true);

  return doc.body.innerHTML;
}

/**
   * Recursively sanitizes DOM nodes
   */
function sanitizeNode(node: Node, root: boolean): void {
  if (node.nodeType === Node.ELEMENT_NODE) {
    const element = node as Element;
    const tagName = element.tagName.toLowerCase();

    if (skippedTags.has(tagName)) {
      // Remove the entire element
      element.parentNode!.removeChild(element);
      return;
    }

    if (!allowedTags.has(tagName) && !root) {
      // Remove disallowed tags but keep their content
      const parent = element.parentNode;
      if (parent) {
        while (element.firstChild) {
          parent.insertBefore(element.firstChild, element);
        }
        parent.removeChild(element);
      }
      return;
    }

    // Filter attributes
    const allowedAttrs = allowedAttributes[tagName] || new Set();
    const attributesToRemove: string[] = [];

    for (let i = 0; i < element.attributes.length; i++) {
      const attr = element.attributes[i];
      if (!allowedAttrs.has(attr.name)) {
        attributesToRemove.push(attr.name);
      } else if (['href', 'src'].includes(attr.name)) {
        // Validate URLs for href and src attributes
        if (!isValidUrl(attr.value)) {
          attributesToRemove.push(attr.name);
        }
      }
    }

    attributesToRemove.forEach(attrName => {
      element.removeAttribute(attrName);
    });
  }

  // Recursively sanitize child nodes
  const children = Array.from(node.childNodes);
  children.forEach(child => sanitizeNode(child, false));
}

/**
   * Validates URLs to prevent XSS attacks
   */
function isValidUrl(url: string): boolean {
  if (!url) return false;

  try {
    const parsed = new URL(url, window.location.origin);
    // Links may only navigate to web URLs. Data URLs are not valid link targets.
    return ['http:', 'https:'].includes(parsed.protocol);
  } catch {
    return false;
  }
}
